import { HttpTypes } from "@medusajs/types"

/** Extract size label from a variant's options array. */
export function getSizeFromVariant(
  variant: HttpTypes.StoreProductVariant
): string {
  if (!variant.options?.length) return variant.title || ""
  const sizeOpt = variant.options.find(
    (o) => o.option?.title?.toLowerCase() === "size"
  )
  return sizeOpt?.value || variant.options[0]?.value || variant.title || ""
}

/**
 * A "fee" line item is a separate (hidden) product line item representing a
 * personalisation charge — e.g. "Name & Number Printing" or a patch — linked
 * back to the real product line item it belongs to via
 * metadata.parent_line_item_id. It should never offer "Edit customisation"
 * itself (editing is keyed off the parent line item's id); it's read-only.
 */
export function getFeeItemDisplay(
  item: HttpTypes.StoreCartLineItem,
  allItems: HttpTypes.StoreCartLineItem[]
) {
  const metadata = (item.metadata as Record<string, unknown> | null) ?? null
  const feeType = typeof metadata?.type === "string" ? metadata.type : null
  const isFeeItem = feeType === "personalization_fee" || feeType === "patch_fee"
  const customName = typeof metadata?.custom_name === "string" ? metadata.custom_name : ""
  const customNumber = typeof metadata?.custom_number === "string" ? metadata.custom_number : ""
  const parentLineItemId =
    typeof metadata?.parent_line_item_id === "string" ? metadata.parent_line_item_id : null
  const parentItem = parentLineItemId
    ? allItems.find((i) => i.id === parentLineItemId) ?? null
    : null

  return { isFeeItem, feeType, customName, customNumber, parentItem }
}

/**
 * Orders cart line items for display: real products sorted newest-first (as
 * before), with each personalisation/patch fee item placed immediately after
 * its parent product — never sorted by its own `created_at`, since a fee item
 * is created a moment after its parent and would otherwise sort above it.
 * A fee item whose parent was removed from the cart is still shown, appended
 * at the end, rather than silently dropped.
 */
export function orderCartItemsWithFees(
  items: HttpTypes.StoreCartLineItem[]
): HttpTypes.StoreCartLineItem[] {
  const isFee = (item: HttpTypes.StoreCartLineItem) => {
    const type = (item.metadata as Record<string, unknown> | null)?.type
    return type === "personalization_fee" || type === "patch_fee"
  }

  const productItems = items.filter((item) => !isFee(item))
  const feeItems = items.filter(isFee)

  const sortedProducts = [...productItems].sort((a, b) =>
    (a.created_at ?? "") > (b.created_at ?? "") ? -1 : 1
  )

  const feesByParent = new Map<string, HttpTypes.StoreCartLineItem[]>()
  feeItems.forEach((fee) => {
    const parentId = (fee.metadata as Record<string, unknown> | null)?.parent_line_item_id
    if (typeof parentId !== "string") return
    const existing = feesByParent.get(parentId) ?? []
    existing.push(fee)
    feesByParent.set(parentId, existing)
  })

  const ordered: HttpTypes.StoreCartLineItem[] = []
  const placedFeeIds = new Set<string>()

  sortedProducts.forEach((product) => {
    ordered.push(product)
    ;(feesByParent.get(product.id) ?? []).forEach((fee) => {
      ordered.push(fee)
      placedFeeIds.add(fee.id)
    })
  })

  feeItems.forEach((fee) => {
    if (!placedFeeIds.has(fee.id)) ordered.push(fee)
  })

  return ordered
}
